import { AccountType } from "@/auth/types/LoginAuth";
import { ACTIONS, Action } from "@/constants/actions";
import { MODULES, Module } from "@/constants/modules";
import { createPermission } from "@/constants/permissions";

/**
 * Map of route paths to their corresponding modules
 */
export const ROUTE_TO_MODULE_MAP: Record<string, Module> = {
  "/dashboard": MODULES.DASHBOARD,
  "/employees": MODULES.EMPLOYEES,
  "/events": MODULES.EVENTS,
  "/integrations": MODULES.INTEGRATIONS,
  "/social-support": MODULES.SOCIAL_ASSISTANCE,
  "/youth-index": MODULES.YOUTH_INDEX,
  "/youth-list": MODULES.YOUTH_LIST,
  "/youth-balance": MODULES.YOUTH_BALANCE,
} as const;

/**
 * Get all possible actions for a module
 * Based on the backend model structure
 */
const getModuleActions = (module: Module): Action[] => {
  switch (module) {
    case MODULES.DASHBOARD:
      return [ACTIONS.VIEW];
    case MODULES.INTEGRATIONS:
      return [ACTIONS.VIEW];
    case MODULES.SOCIAL_ASSISTANCE:
      return [ACTIONS.VIEW, ACTIONS.EDIT, ACTIONS.ADD, ACTIONS.DELETE];
    case MODULES.EMPLOYEES:
      return [ACTIONS.VIEW, ACTIONS.EDIT, ACTIONS.ADD, ACTIONS.DELETE];
    case MODULES.EVENTS:
      return [ACTIONS.VIEW, ACTIONS.EDIT, ACTIONS.ADD, ACTIONS.DELETE];
    case MODULES.YOUTH_INDEX:
      return [ACTIONS.VIEW];
    case MODULES.YOUTH_LIST:
      return [ACTIONS.VIEW, ACTIONS.VIEW_PERSONAL_DATA];
    case MODULES.YOUTH_BALANCE:
      return [ACTIONS.VIEW];
    default:
      return [];
  }
};

/**
 * Check if user has at least one permission for a module
 * @param module - Module to check
 * @param userPermissions - Array of user's permission strings
 * @returns boolean - true if user has at least one permission for the module
 */
export const hasModulePermission = (
  module: Module,
  userPermissions: string[],
): boolean => {
  if (!userPermissions || userPermissions.length === 0) {
    return false;
  }

  const moduleActions = getModuleActions(module);

  // Check if user has at least one action permission for this module
  return moduleActions.some((action) => {
    const permission = createPermission(module, action);
    return userPermissions.includes(permission);
  });
};

/**
 * Check if a route should be shown based on user permissions
 * @param routePath - Route path (e.g., "/dashboard", "/employees")
 * @param userPermissions - Array of user's permission strings
 * @param accountType - User's account type (optional)
 * @returns boolean - true if route should be shown
 */
export const shouldShowRoute = (
  routePath: string,
  userPermissions: string[],
  accountType?: AccountType | null,
  accessLevel?: string | null,
): boolean => {
  // Settings route is visible to all authenticated users.
  // Child-level restrictions are handled in sidebar and page guards.
  if (routePath === "/settings") {
    return true;
  }

  // Opportunities and Yetakchi are only visible
  // to republic-level employees and superusers
  if (routePath === "/opportunities" || routePath === "/yetakchi") {
    return accountType === "SUPER_ADMIN" || accessLevel === "republic";
  }

  // Social Support is visible to all access levels
  if (routePath === "/social-support") {
    return (
      accountType === "SUPER_ADMIN" ||
      accessLevel === "republic" ||
      accessLevel === "region" ||
      accessLevel === "city" ||
      accessLevel === "makhalla"
    );
  }

  const module = ROUTE_TO_MODULE_MAP[routePath];
  if (!module) {
    // If route is not in the map, show it by default (backward compatibility)
    return true;
  }

  return hasModulePermission(module, userPermissions);
};
